import type { ClientFlowAction } from '~/graphql/types.client';
import getMessageForMetadataRequiringActions from '../../utils/getMessageForMetadataRequiringActions';
import getDirectChildActions from '~/components/page/Automation/v2/components/Builder/utils/getDirectChildActions';
import type { OptionsArgs } from '../..';
import type { OptionOf } from '~/components/molecule/Dropdown';
import isMetadataRequiredAction from '~/typeguards/isMetadataRequiredAction';
import { FlowV2_Update_Action } from '~/graphql/types';

const adjustFlowMetadataForDeletedBranch = (args: OptionsArgs) => {
  const branchChildren = getDirectChildActions({
    action: args.action,
    actions: args.actions,
  });

  const actionsThatRequireMetadata = branchChildren.filter(action =>
    isMetadataRequiredAction(action),
  );

  if (actionsThatRequireMetadata.length > 0) {
    args.setFlowMetadata(prev => {
      // remove previously set metadata for the actions that require metadata
      const filteredPrevious = prev.filter(
        a =>
          !actionsThatRequireMetadata.some(
            action => action.id === a.flowBlueprintActionId,
          ),
      );

      // Drop the contacts from these actions
      return [
        ...filteredPrevious,
        ...actionsThatRequireMetadata.map(action => ({
          flowBlueprintActionId: action.id,
          action: FlowV2_Update_Action.Drop,
        })),
      ];
    });
  }

  const branchIds = new Set([args.action.id, ...branchChildren.map(a => a.id)]);

  args.setActions(prevActions => {
    const remaining = prevActions
      .filter(a => !branchIds.has(a.id))
      .map(a => {
        if (a.__typename === 'FlowV2_Action_Start') return a;

        let updated = { ...a };

        if ('parentIds' in updated) {
          updated = {
            ...updated,
            parentIds: updated.parentIds.filter(pid => !branchIds.has(pid)),
          };
        }

        if (updated.__typename === 'FlowV2_Action_IfElse') {
          updated = {
            ...updated,
            trueChildId:
              updated.trueChildId && branchIds.has(updated.trueChildId)
                ? null
                : updated.trueChildId,
            falseChildId:
              updated.falseChildId && branchIds.has(updated.falseChildId)
                ? null
                : updated.falseChildId,
          };
        }

        return updated;
      });

    return remaining as Array<ClientFlowAction>;
  });

  args.setLastDeletedActionNextChildId(null);
};

const getDeleteBranchOption = (
  args: OptionsArgs,
): OptionOf<(action: ClientFlowAction) => void> => ({
  key: 'deleteBranch',
  label: 'Branch verwijderen',
  icon: { name: 'branches' },
  type: 'DANGER',
  payload: () => {
    const branchChildren = getDirectChildActions({
      action: args.action,
      actions: args.actions,
    });

    const count = branchChildren.length + 1;

    const extraWarning = getMessageForMetadataRequiringActions({
      branchChildren,
    });

    args.setDialog({
      type: 'dialogConfirm',
      modalHeader: 'Branch verwijderen',
      modalDescription: `Weet je zeker dat je deze branch wilt verwijderen? Dit verwijdert ${count} ${count === 1 ? 'actie' : 'acties'}. ${extraWarning}`,
      onConfirm: () => {
        adjustFlowMetadataForDeletedBranch(args);
        args.setDialog(null);
      },
      onCancel: () => args.setDialog(null),
    });
  },
});

export default getDeleteBranchOption;
